/**
 * TanStack Router compatibility hooks (legacy react-router-dom call shapes).
 */
import { useCallback } from 'react';
import {
  useNavigate as useNavigateTS,
  useRouterState,
  useParams as useParamsTS,
  Link as LinkTS,
  Navigate as NavigateTS,
  Outlet as OutletTS,
} from '@tanstack/react-router';

/** TanStack expects navigate({ to }); legacy code uses navigate('/path'). */
function useTanStackNavigateShim() {
  const navigate = useNavigateTS();
  return useCallback(
    (to, options) => {
      if (typeof to === 'number') {
        window.history.go(to);
        return;
      }
      if (typeof to === 'string') {
        const [pathname, query = ''] = to.split('?');
        const search = {};
        if (query) {
          new URLSearchParams(query).forEach((value, key) => {
            search[key] = value;
          });
        }
        return navigate({
          to: pathname,
          ...(Object.keys(search).length ? { search } : {}),
          replace: options?.replace,
          state: options?.state,
        });
      }
      return navigate(to);
    },
    [navigate],
  );
}

export function useNavigate() {
  return useTanStackNavigateShim();
}

export function useLocation() {
  const location = useRouterState({ select: (s) => s.location });
  return {
    pathname: location.pathname,
    search: location.searchStr ?? '',
    hash: location.hash,
    state: location.state,
    key: location.state?.key ?? 'default',
  };
}

export function useParams() {
  return useParamsTS({ strict: false });
}

export function useSearchParams() {
  const navigate = useNavigateTS();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr }) ?? '';
  const searchParams = new URLSearchParams(searchStr);

  const setSearchParams = (nextInit, navigateOptions) => {
    const next =
      typeof nextInit === 'function'
        ? nextInit(new URLSearchParams(searchStr))
        : new URLSearchParams(nextInit);
    const search = {};
    next.forEach((value, key) => {
      search[key] = value;
    });
    navigate({
      to: pathname,
      search,
      replace: navigateOptions?.replace,
    });
  };

  return [searchParams, setSearchParams];
}

function TanStackNavLink({
  className,
  activeClassName,
  style,
  activeStyle,
  children,
  end,
  ...rest
}) {
  if (typeof className === 'function') {
    return (
      <LinkTS
        {...rest}
        activeOptions={{ exact: end ?? false }}
        className={className}
        style={style}
        activeProps={{
          className: activeClassName,
          style: activeStyle,
        }}
      >
        {children}
      </LinkTS>
    );
  }
  return (
    <LinkTS
      {...rest}
      activeOptions={{ exact: end ?? false }}
      className={className}
      activeProps={{
        className: activeClassName,
        style: activeStyle,
      }}
      style={style}
    >
      {children}
    </LinkTS>
  );
}

export const Link = LinkTS;
export const NavLink = TanStackNavLink;
export const Navigate = NavigateTS;
export const Outlet = OutletTS;
